import {
  cloneElement,
  FunctionComponent,
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Box, Menu, MenuItem } from '@black-bear-energy/rucksack'
import { usePreviewChecklistDocument } from '../../api/hooks/usePreviewChecklistDocument'
import { useNotifications } from '../../state/modules/notifications'
import { useProgressToast } from '../../api/hooks/helpers/useProgressToast'
import { PreparedFile } from '../helpers/DocumentExchange/extractDocumentFiles'
import { TextWithTooltip } from './TextWithTooltip'
import { MenuButtonSection } from './MenuButtonSection'

// Opening a blob: URL in a new tab, or fetching one for a browser download, is
// an async operation - if a previously opened tab/download is still reading a
// URL when we revoke it, that read fails with ERR_FILE_NOT_FOUND. Defer
// revocation to a later tick, so the browser has already read the blob data
// before the URL is revoked.
const revokeFilesDeferred = (files: PreparedFile[]) => {
  setTimeout(() => {
    files.forEach((file) => URL.revokeObjectURL(file.url))
  }, 0)
}

export const FilesPreviewMenu: FunctionComponent<FilesPreviewMenuProps> = ({
  documentId,
  documentName,
  isMainMenuOpen,
  onRequestClose,
  children,
}) => {
  const [previewFiles, setPreviewFiles] = useState<PreparedFile[]>([])
  const [innerAnchorEl, setInnerAnchorEl] = useState<null | HTMLElement>(null)
  // Mirrors the parent menu's open state for use inside the preview
  // fetch's onSettled callback - a fetch that resolves after the user
  // dismissed that menu can discard its stale result instead of leaking blob
  // URLs or reopening the files menu unexpectedly.
  const isMainMenuClosedRef = useRef(isMainMenuOpen === false)
  isMainMenuClosedRef.current = isMainMenuOpen === false
  // Read by the reset effect below without adding previewFiles to its
  // dependency list, so the effect only re-runs when the outer menu's open
  // state actually flips.
  const previewFilesRef = useRef(previewFiles)
  previewFilesRef.current = previewFiles
  const { mutate: previewChecklistDocument, isPending: isPreviewLoading } =
    usePreviewChecklistDocument()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  const resetFilesMenu = useCallback(() => {
    revokeFilesDeferred(previewFilesRef.current)
    setPreviewFiles([])
    setInnerAnchorEl(null)
  }, [])

  // When this menu is nested inside another one, closing it closes the whole
  // chain (rather than leaving the outer menu open behind it), matching how
  // native cascading menus (e.g. a browser's right-click menu) dismiss
  // together.
  const closeFilesMenu = () => {
    resetFilesMenu()
    onRequestClose?.()
  }

  useEffect(() => {
    if (isMainMenuOpen === false) {
      resetFilesMenu()
    }
  }, [isMainMenuOpen, resetFilesMenu])

  const trigger =
    typeof children === 'function'
      ? children({ isLoading: isPreviewLoading })
      : children

  const triggerProps: FilesPreviewTriggerProps = {
    disabled: isPreviewLoading,
    onClick: (event) => {
      const anchorEl = event.currentTarget
      previewChecklistDocument(
        { documentId, documentName },
        {
          onSettled: (files) => {
            if (isMainMenuClosedRef.current) {
              // The containing menu was dismissed while this fetch was in
              // flight - discard the stale result instead of leaking blob
              // URLs or reopening the files menu unexpectedly.
              files?.forEach((file) => URL.revokeObjectURL(file.url))
              return
            }
            if (files) {
              setPreviewFiles(files)
              setInnerAnchorEl(anchorEl)
            }
          },
        }
      )
    },
  }

  return (
    <>
      {cloneElement(trigger, triggerProps)}
      <Menu
        anchorEl={innerAnchorEl}
        open={Boolean(innerAnchorEl)}
        onClose={closeFilesMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiList-root': { py: 0 },
          // Shift the menu 4px further from the main menu's edge so the two
          // don't sit flush against each other.
          '& .MuiPaper-root': { marginLeft: '-4px' },
        }}
      >
        <Box component="li" sx={{ paddingY: 2 }}>
          <MenuButtonSection title="Files">
            <Box
              sx={{
                minWidth: 220,
                maxWidth: 280,
                maxHeight: 320,
                overflowY: 'auto',
                '& .MuiMenuItem-root': {
                  display: 'flex',
                  padding: '6px 16px',
                  alignItems: 'center',
                  gap: '12px',
                },
              }}
            >
              {previewFiles.map((file) => (
                <MenuItem
                  key={file.fileName}
                  className="nav-link-unstyled"
                  component="a"
                  href={file.url}
                  target={file.fileType ? '_blank' : undefined}
                  download={file.fileType ? undefined : file.fileName}
                  rel="noopener noreferrer"
                  onMouseUp={(event) => {
                    // Prevent MenuButton's keepOpen={false} close-on-click from
                    // firing here - closing immediately would unmount this
                    // anchor before the browser processes its native default
                    // action (download save / new-tab navigation). Close it
                    // ourselves below instead, once that's had a chance to run.
                    event.stopPropagation()
                  }}
                  onClick={() => {
                    let toastId: string | undefined
                    if (!file.fileType) {
                      // Non-previewable files trigger a native browser download
                      // with no completion callback we can observe, so this is
                      // the only feedback the user gets that the click did
                      // something - previewable files get their own new tab.
                      toastId = startProgress(
                        'Downloading Document',
                        file.fileName
                      )
                    }
                    // The browser already started this click's native action
                    // (download save / new-tab navigation) as part of
                    // dispatching the click event - deferring the close to a
                    // later tick just avoids unmounting the anchor while
                    // that's still in progress.
                    setTimeout(() => {
                      if (toastId) {
                        endProgress(toastId)
                        sendNotification({
                          title: 'Download Successful',
                          message: `${file.fileName} has been successfully downloaded.`,
                          useRucksackUI: true,
                        })
                      }
                      closeFilesMenu()
                    }, 0)
                  }}
                >
                  <TextWithTooltip
                    text={file.fileName}
                    maxLineCount={1}
                    sx={{ fontSize: '1rem', minWidth: 0 }}
                  />
                </MenuItem>
              ))}
            </Box>
          </MenuButtonSection>
        </Box>
      </Menu>
    </>
  )
}

FilesPreviewMenu.displayName = 'FilesPreviewMenu'

/**
 * Props injected into the trigger element. Any handlers already on that
 * element are replaced, so the trigger should be a presentational element
 * (a button, a menu item) rather than one with click behavior of its own.
 */
interface FilesPreviewTriggerProps {
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLElement>
  onMouseUp?: MouseEventHandler<HTMLElement>
}

type FilesPreviewTrigger = ReactElement<FilesPreviewTriggerProps>

interface FilesPreviewMenuProps {
  documentId: number
  documentName: string
  /**
   * The element the user clicks to fetch and open the file list. Pass a
   * function instead of an element to render the trigger differently while
   * the fetch is in flight (e.g. swapping an icon for a spinner).
   */
  children:
    | FilesPreviewTrigger
    | ((state: { isLoading: boolean }) => FilesPreviewTrigger)
  /**
   * Open state of the menu this one is nested inside, if any. When that menu
   * closes, the file list is torn down with it. Omit when the trigger isn't
   * inside another menu.
   */
  isMainMenuOpen?: boolean
  /**
   * Called when the file list closes, so a containing menu can close too.
   * Omit when the trigger isn't inside another menu.
   */
  onRequestClose?: () => void
}
